import { pageTitle } from '@palaccount/config';
import type { EChartsOption } from 'echarts';
import { Info, Star } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { TableCell } from '@/components/ui/table';
import { formatMoney } from '@/lib/format';

import { Amount, Ltr, Pct } from '@/components/amount';
import { Chart } from '@/components/chart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import type { ReportConfigBody } from '@palaccount/contracts';

import { useWarehouses } from '@/features/inventory/stock-api';

import { useSalesReport } from './api';
import { configInForce } from './column-picker';
import { DetailTable, type DetailColumn } from './detail-table';
import { buildReportGrid, downloadXlsx } from './export';
import { DetailFold, MoreFilters, PRESETS, ReportToolbar, SummaryCards, useReportWindow, type PresetKey } from './report-shell';
import { ReportsTabs } from './reports-tabs';
import { WarehouseFilter } from './warehouse-filter';

/** DSH-05 detail-table row — the full R13 column set. */
type DetailRow = {
  invoiceId: string;
  date: string;
  invoiceNo: string;
  customer: string;
  salesperson: string;
  warehouse: string;
  lines: number;
  /** The DOCUMENT's own amount, in `currency` (GEN-108). */
  total: string;
  currency: string;
  /** The same amount at that document's frozen rate — the addable one. */
  totalBase: string;
  status: 'paid' | 'partial' | 'overdue' | 'open';
};

const DEFAULT_COLUMNS = ['date', 'invoiceNo', 'customer', 'salesperson', 'warehouse', 'lines', 'total', 'currency', 'status'];

/** DSH-05 — Sales Reports on the DSH-10 skeleton. */
export function SalesReportPage() {
  /**
   * GEN-107: what these figures are IN, from the session rather than a literal.
   *
   * The comment that stood here said "nothing here converts, so GEN-116 keeps
   * the disclosure off the screen." It was false — every figure on the screen
   * was a sum of invoice totals across currencies, which is not a conversion
   * but an ADDITION of unlike things, and F-27 forbids that outright. The
   * sentence was true only in the sense that no conversion had been attempted.
   * Now the server converts each invoice at its own frozen rate and says so
   * when it did (`conversionNote`), and this comment describes the screen on
   * disk rather than the one its author had in mind.
   */
  const baseCurrency = useBaseCurrency();
  const { t, i18n } = useTranslation();
  const { from, to, setFrom, setTo } = useReportWindow('thisYear');
  const [preset, setPreset] = useState<PresetKey>('thisYear');
  const [sort, setSort] = useState<'total' | 'invoices' | 'avg'>('total');
  const [topN, setTopN] = useState<10 | 20 | 999>(10);
  const [drill, setDrill] = useState<string | null>(null);
  const [columns, setColumns] = useState<string[]>(DEFAULT_COLUMNS);
  /** BR-DSH-10-17's filter. `null` is "All warehouses", the delivered default. */
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  /**
   * BR-DSH-10-16 / DSH-10 Band 6 — which saved configuration the reader
   * APPLIED, so the printed heading can name it while it is still in force.
   * `configInForce` decides "still": the name is claimed by comparing the
   * page's current choices against what the configuration set, never by
   * remembering the click alone.
   */
  const [appliedConfig, setAppliedConfig] = useState<{ name: string; body: ReportConfigBody } | null>(null);

  /*
   * BR-DSH-10-07: "changing any filter recomputes every card, chart and
   * table." That is one line here rather than a re-fetch call, because the
   * filter is part of the query KEY — so the whole screen follows it, and the
   * detail table and the exports below follow it too (BR-DSH-10-24) without
   * anything having to remember to.
   *
   * The key is spread conditionally so that "All warehouses" sends no
   * `warehouseId` at all, rather than an empty one the server would have to
   * have an opinion about.
   */
  const { data, isPending, isError } = useSalesReport({ from, to, ...(warehouseId ? { warehouseId } : {}) });

  useEffect(() => {
    document.title = pageTitle(t('reports.tabSales'));
  }, [t]);

  const trendOption = useMemo<EChartsOption>(() => {
    const trend = data?.trend ?? [];
    return {
      grid: { left: 8, right: 8, top: 24, bottom: 24, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: trend.map((m) => m.month) },
      yAxis: { type: 'value' },
      series: [
        {
          type: 'bar',
          data: trend.map((m) => Number(m.revenue)),
          itemStyle: { color: '#2563eb' },
          label: { show: true, position: 'top' },
        },
      ],
    };
  }, [data?.trend]);

  const donutOption = useMemo<EChartsOption>(() => {
    const cats = data?.byCategory ?? [];
    return {
      tooltip: { trigger: 'item' },
      series: [
        {
          type: 'pie',
          radius: ['45%', '75%'],
          data: cats.map((c) => ({ name: c.name, value: Number(c.revenue) })),
        },
      ],
    };
  }, [data?.byCategory]);

  const detailColumns: DetailColumn<DetailRow>[] = [
    { id: 'date', label: t('reports.colDate'), render: (r) => <Ltr value={r.date} className="font-mono" /> },
    /*
      ERR-DSH-05-04 / ERR-DSH-05-07, and the build had REPRODUCED it: "Invoice
      numbers in the detail table are styled as links but open nothing" —
      §5's own field note calls the cell "monospaced and BRAND BLUE, WHICH
      READS AS A LINK. It is not clickable in the prototype." The register in
      PART 21 lists it as a defect to fix, not a behaviour to copy.

      A blue underlinable number that does nothing is worse than a black one:
      the colour is a promise, the reader spends a click finding out it was
      not kept, and §14's advice was to go and find the invoice by hand. The
      row already carries `invoiceId` and `/sales/:invoiceId` is the same
      route DSH-02's Overdue Invoices arrow uses, so the destination existed
      all along — only the anchor was missing.
    */
    {
      id: 'invoiceNo',
      label: t('reports.colInvoice'),
      render: (r) => (
        <Link to={`/sales/${r.invoiceId}`} className="font-mono text-primary hover:underline">
          <Ltr value={r.invoiceNo} />
        </Link>
      ),
    },
    { id: 'customer', label: t('reports.colCustomer'), render: (r) => r.customer },
    { id: 'salesperson', label: t('reports.colSalesperson'), render: (r) => r.salesperson },
    { id: 'warehouse', label: t('reports.colWarehouse'), render: (r) => r.warehouse },
    { id: 'lines', label: t('reports.colItems'), numeric: true, render: (r) => r.lines },
    /*
     * THE ROW'S OWN CURRENCY, NOT THE REPORT'S. `total` is what the document
     * itself says (GEN-108), so rendering it against `baseCurrency` would
     * print "$45,900.00" on a euro invoice — a wrong LABEL in place of the
     * wrong TOTAL this sweep removed, which is the same defect in different
     * clothes. `Amount` takes the currency per row for exactly this case.
     */
    { id: 'total', label: t('reports.colTotal'), numeric: true, render: (r) => <Amount value={r.total} currency={r.currency} /> },
    /*
     * N-14's rule, applied to money instead of units: "one column, many units,
     * no header that could be true of the whole of it" — so the marker goes on
     * the ROW. On screen `Amount` already prints the symbol, but the XLSX and
     * PDF exports run every numeric cell through `bareNumber`, which strips it;
     * without this column a spreadsheet of the Total column would be mixed
     * currencies as bare numbers, which is F-27's defect in another medium.
     * Default-visible for that reason, and constant (and harmless) for the
     * single-currency company.
     */
    { id: 'currency', label: t('reports.colCurrency'), render: (r) => r.currency },
    /*
     * The converted figure, as its own column: this is the one the cards above
     * add (BR-DSH-05-09), so a reader who wants the rows to reconcile with the
     * headline can show it. Not in DEFAULT_COLUMNS — for a single-currency
     * company it is identical to the column beside it, and most companies are
     * that one. R13's picker is what makes an optional column the right shape.
     */
    { id: 'totalBase', label: t('reports.colTotalBase'), numeric: true, render: (r) => <Amount value={r.totalBase} currency={baseCurrency} /> },
    {
      id: 'status',
      label: t('reports.colStatus'),
      render: (r) => (
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs',
            r.status === 'paid' && 'bg-emerald-100 text-emerald-700',
            r.status === 'partial' && 'bg-amber-100 text-amber-700',
            r.status === 'overdue' && 'bg-red-100 text-red-700',
            r.status === 'open' && 'bg-muted text-muted-foreground',
          )}
        >
          {t(`reports.status_${r.status}`)}
        </span>
      ),
    },
  ];

  /**
   * What is on screen, as ONE grid — the single input Print, Excel and PDF all
   * take (BR-DSH-10-24). `buildReportGrid` resolves the visible columns
   * through the SAME `resolveColumns` the detail table uses, so a column the
   * picker hid cannot reappear in an export.
   */
  const rtl = i18n.language === 'ar';
  /**
   * BR-DSH-10-16 — the heading block's "filters applied", stated even when
   * the answer is All: the printed sheet must say what the figures were
   * narrowed by, and "All warehouses" is that answer, not the absence of one.
   * The name comes from the same master-backed list the filter itself offers
   * (BR-DSH-10-17); react-query dedupes the read with `WarehouseFilter`'s.
   */
  const { data: warehouses } = useWarehouses({ status: 'active' });
  const warehouseInForce =
    (warehouseId && warehouses?.find((w) => w.id === warehouseId)?.name) || t('reports.allWarehouses');
  const currentBody: ReportConfigBody = { columns, filters: {}, period: { preset, from, to } };
  const configName =
    appliedConfig !== null && configInForce(appliedConfig.body, currentBody) ? appliedConfig.name : null;
  const { grid, types } = buildReportGrid<DetailRow>({
    title: t('reports.tabSales'),
    from,
    to,
    filters: [{ label: t('reports.filterWarehouse'), value: warehouseInForce }],
    configName,
    cards: data
      ? [
          { label: t('reports.cardRevenue'), value: formatMoney(data.summary.totalRevenue.value, 'USD'), changePct: data.summary.totalRevenue.changePct },
          { label: t('reports.cardInvoices'), value: String(data.summary.invoices.value), changePct: data.summary.invoices.changePct },
          { label: t('reports.cardAvgInvoice'), value: formatMoney(data.summary.avgInvoice.value, 'USD'), changePct: data.summary.avgInvoice.changePct },
          { label: t('reports.cardItemsSold'), value: String(data.summary.itemsSold.value), changePct: data.summary.itemsSold.changePct },
        ]
      : [],
    columnDefs: detailColumns,
    selected: columns,
    defaults: DEFAULT_COLUMNS,
    rows: data?.rows ?? [],
    // The RAW value, except for the one column whose screen text is a label
    // rather than the value stored behind it.
    value: (row, id) => (id === 'status' ? t(`reports.status_${row.status}`) : String(row[id as keyof DetailRow] ?? '')),
    dateColumns: ['date'],
  });

  /**
   * BR-DSH-05-08's second half: the salesperson table "must RE-SORT when the
   * sort dropdown changes". Re-sorting in the browser rather than re-asking
   * the server is what makes the star below always mark the row the reader is
   * looking at — the first row of the CURRENT order, not the first row of the
   * order the server happened to send. A star pinned to the server's ordering
   * would keep decorating the top biller after the reader switched to "by
   * average", which is a plausible-looking row and the wrong one.
   *
   * The copy before `.sort` is not tidiness: `data.bySalesperson` is react-
   * query's cached array, and sorting it in place mutates the cache.
   */
  const sortedSalespeople = useMemo(() => {
    const list = [...(data?.bySalesperson ?? [])];
    return list.sort((a, b) =>
      sort === 'total' ? Number(b.total) - Number(a.total) : sort === 'invoices' ? b.invoices - a.invoices : Number(b.avg) - Number(a.avg),
    );
  }, [data?.bySalesperson, sort]);

  return (
    <div className="flex flex-col gap-4">
      <ReportsTabs />
      <h1 className="text-xl font-semibold">{t('reports.tabSales')}</h1>

      <ReportToolbar
        // BR-DSH-10-02 — this report has a window and every figure covers it:
        // `from` and `to` ride in the query key below and reach the server.
        window={{ presets: PRESETS, preset, onPreset: setPreset, from, to, onFrom: setFrom, onTo: setTo }}
        grid={data ? grid : null}
        onExcel={() =>
          // BR-DSH-10-24: the export mirrors the columns in force — the same
          // grid Print and PDF use, so all three cannot disagree.
          downloadXlsx(`sales-report-${from}-${to}.xlsx`, t('reports.tabSales'), grid.columns, grid.rows, types, rtl)
        }
      />

      {/*
        BR-DSH-10-17 — the Warehouse / Branch filter, reading the master. See
        `warehouse-filter.tsx` for what "warehouses that hold stock" was taken
        to mean and why; ERR-DSH-10-03 is the standalone three-row list it
        replaces, two of whose three names existed nowhere in the product.
      */}
      <MoreFilters>
        <WarehouseFilter value={warehouseId} onChange={setWarehouseId} />
        <p className="text-xs text-muted-foreground sm:col-span-2 lg:col-span-3">{t('reports.filtersHint')}</p>
      </MoreFilters>

      {isPending ? (
        <div className="h-60 animate-pulse rounded-md bg-muted" aria-busy />
      ) : isError || !data ? (
        <p role="alert" className="py-8 text-center text-sm text-destructive">{t('common.loadFailed')}</p>
      ) : (
        <>
          {/*
            GEN-115 — "directly above the numbers", which on this screen means
            above the summary cards and therefore above everything. GEN-116
            hides it when nothing converted and GEN-117 fixes the informational
            grey; both are the SERVER's decision arriving as a nullable string,
            because the browser cannot know whether a conversion happened.

            It names the basis rather than a rate date: every invoice on this
            report converts at the rate IT froze, so there is no single date to
            print (EC-19-10). Same sentence as the partner statement's, on
            purpose — two screens doing the same arithmetic should not describe
            it two ways.
          */}
          {data.conversionNote === null ? null : (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5" />
              {data.conversionNote}
            </p>
          )}
          <SummaryCards
            cards={[
              { label: t('reports.cardRevenue'), value: data.summary.totalRevenue.value, money: true, changePct: data.summary.totalRevenue.changePct },
              { label: t('reports.cardInvoices'), value: String(data.summary.invoices.value), changePct: data.summary.invoices.changePct },
              { label: t('reports.cardAvgInvoice'), value: data.summary.avgInvoice.value, money: true, changePct: data.summary.avgInvoice.changePct },
              { label: t('reports.cardItemsSold'), value: String(data.summary.itemsSold.value), changePct: data.summary.itemsSold.changePct },
            ]}
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('reports.salesTrend')}</CardTitle>
            </CardHeader>
            <CardContent>
              {data.trend.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
              ) : (
                <Chart option={trendOption} height={220} />
              )}
            </CardContent>
          </Card>

          <div className="grid gap-[18px] lg:grid-cols-[1.3fr_1fr]">
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm">{t('reports.bySalesperson')}</CardTitle>
                <select
                  aria-label={t('reports.sortBy')}
                  className="rounded-md border bg-background px-2 py-1 text-xs"
                  value={sort}
                  onChange={(e) => setSort(e.target.value as typeof sort)}
                >
                  <option value="total">{t('reports.sortTotal')}</option>
                  <option value="invoices">{t('reports.sortInvoices')}</option>
                  <option value="avg">{t('reports.sortAvg')}</option>
                </select>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="pb-1 text-start">{t('reports.colSalesperson')}</th>
                      <th className="pb-1 text-end">{t('reports.colTotal')}</th>
                      <th className="pb-1 text-end">{t('reports.colInvoices')}</th>
                      <th className="pb-1 text-end">{t('reports.colAvg')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/*
                      BR-DSH-05-08's first half: "the salesperson table must
                      mark the FIRST ROW with a star and TINT IT". Two channels
                      for one fact, which is GEN-61 / NFR-39's rule that colour
                      is never the only way something is said — the tint alone
                      would vanish for a reader who cannot see it.
                    */}
                    {sortedSalespeople.map((row, index) => (
                      <tr key={row.id ?? row.name} className={cn('border-t', index === 0 && 'bg-primary/5')}>
                        <TableCell className="flex items-center gap-1 px-0">
                          {index === 0 ? <Star className="h-3.5 w-3.5 fill-amber-600 text-amber-600" /> : null}
                          {row.name}
                        </TableCell>
                        <TableCell className="px-0 text-end font-medium"><Amount value={row.total} currency={baseCurrency} /></TableCell>
                        <TableCell className="px-0 text-end text-muted-foreground">{row.invoices}</TableCell>
                        <TableCell className="px-0 text-end text-muted-foreground"><Amount value={row.avg} currency={baseCurrency} /></TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('reports.byCategory')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.byCategory.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">{t('reports.noRows')}</p>
                ) : (
                  <>
                    <Chart option={donutOption} height={150} />
                    <ul className="mt-2 flex flex-col gap-1 text-sm">
                      {data.byCategory.map((cat) => (
                        <li key={cat.id ?? cat.name}>
                          <button
                            type="button"
                            className={cn('flex w-full justify-between rounded px-1 hover:bg-muted', drill === (cat.id ?? cat.name) && 'bg-muted')}
                            onClick={() => setDrill(drill === (cat.id ?? cat.name) ? null : (cat.id ?? cat.name))}
                          >
                            <span>{cat.name}</span>
                            <span className="text-muted-foreground">
                              <Pct value={cat.share} className="font-mono" />
                            </span>
                          </button>
                          {/*
                            BR-DSH-05-11: "Clicking a category must list EVERY
                            item inside it WITH ITS REVENUE." Every item, not
                            a top-n — the server sends the category's whole
                            item list with no LIMIT, because a drill-down that
                            silently truncates is worse than none: the reader
                            asked what is inside and would be shown part of it
                            with nothing saying so.
                          */}
                          {drill === (cat.id ?? cat.name) ? (
                            <ul className="ms-3 mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
                              {cat.items.map((item) => (
                                <li key={item.name} className="flex justify-between">
                                  <span>{item.name}</span>
                                  <Amount value={item.revenue} currency={baseCurrency} className="text-xs" />
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-xs text-muted-foreground">{t('reports.drillHint')}</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-[18px] lg:grid-cols-2">
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm">{t('reports.topCustomers')}</CardTitle>
                <TopNSwitch topN={topN} onTopN={setTopN} />
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <tbody>
                    {data.topCustomers.slice(0, topN).map((row, index) => (
                      <tr key={row.id ?? row.name} className="border-t">
                        <TableCell className="px-0 text-muted-foreground">{index + 1}</TableCell>
                        <TableCell className="px-0">{row.name}</TableCell>
                        <TableCell className="px-0 text-end font-medium"><Amount value={row.total} currency={baseCurrency} /></TableCell>
                        <TableCell className="px-0 text-end text-muted-foreground">{row.invoices}</TableCell>
                        <TableCell className="px-0 text-end text-xs text-muted-foreground">
                          {row.lastPurchase ? <Ltr value={row.lastPurchase} className="font-mono" /> : '—'}
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm">{t('reports.topItems')}</CardTitle>
                <TopNSwitch topN={topN} onTopN={setTopN} />
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <tbody>
                    {data.topItems.slice(0, topN).map((row, index) => (
                      <tr key={row.id ?? row.name} className="border-t">
                        <TableCell className="px-0 text-muted-foreground">{index + 1}</TableCell>
                        <TableCell className="px-0 font-mono text-xs text-muted-foreground"><Ltr value={row.code} /></TableCell>
                        <TableCell className="px-0">{row.name}</TableCell>
                        <TableCell className="px-0 text-end text-muted-foreground"><Ltr value={row.qty} className="font-mono" /></TableCell>
                        <TableCell className="px-0 text-end font-medium"><Amount value={row.revenue} currency={baseCurrency} /></TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('reports.byWarehouse')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {data.byWarehouse.map((row) => {
                const max = Math.max(...data.byWarehouse.map((w) => Number(w.value)), 1);
                return (
                  <div key={row.name} className="flex items-center gap-2 text-sm">
                    <span className="w-40 truncate">{row.name}</span>
                    <div className="h-[22px] flex-1 rounded bg-muted">
                      <div className="h-full rounded bg-primary" style={{ width: `${(Number(row.value) / max) * 100}%` }} />
                    </div>
                    <Amount value={row.value} currency={baseCurrency} className="text-sm font-medium" />
                    <span className="text-xs text-muted-foreground">{t('reports.invCount', { count: row.count })}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <DetailFold count={data.rows.length}>
            <DetailTable<DetailRow>
              report="sales"
              columns={detailColumns}
              defaults={DEFAULT_COLUMNS}
              selected={columns}
              onSelect={setColumns}
              rows={data.rows}
              rowKey={(r) => r.invoiceId}
              currentConfig={() => ({ columns, filters: {}, period: { preset, from, to } })}
              onApplyConfig={(config, name) => {
                if (config.period.from) setFrom(config.period.from);
                if (config.period.to) setTo(config.period.to);
                setPreset((config.period.preset as PresetKey) || 'custom');
                // BR-DSH-10-16: remember WHICH configuration, for the heading.
                setAppliedConfig({ name, body: config });
              }}
            />
          </DetailFold>
        </>
      )}
    </div>
  );
}

function TopNSwitch({ topN, onTopN }: { topN: 10 | 20 | 999; onTopN: (n: 10 | 20 | 999) => void }) {
  const { t } = useTranslation();
  return (
    <span className="flex gap-1 text-xs">
      {([10, 20, 999] as const).map((n) => (
        <button
          key={n}
          type="button"
          className={cn('rounded border px-1.5 py-0.5', topN === n && 'border-primary text-primary')}
          onClick={() => onTopN(n)}
        >
          {n === 999 ? t('reports.all') : n}
        </button>
      ))}
    </span>
  );
}
